"use client"
import { zodResolver } from "@hookform/resolvers/zod"
import type { FragmentOf, ResultOf } from "@opensea/graphql"
import { graphql, readFragment, useQuery } from "@opensea/graphql"
import { useToasts } from "@opensea/ui-kit"
import { useLocalStorage } from "@opensea/ui-kit/hooks"
import { useCallback, useMemo, useState } from "react"
import { FormProvider, useForm, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import {
  isItemTradable,
  isItemTradableFragment,
} from "../../../../../../apps/web/components/items/validation/tradable"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants"
import { useSyncFormFields } from "../../../hooks/useSyncFormField"
import { useAddress } from "../../../polychain/hooks"
import { FlowActions, FlowState } from "../../../stores/flowStore"
import type { ListingFlowState } from "../../flow-state"
import { useEndFlowOnAccountChange } from "../../hooks/useEndFlowOnAccountChange"
import {
  useLiveUpdateItemsBestOrders,
  useLiveUpdateItemsBestOrdersFragment,
} from "../../hooks/useLiveUpdateItemsBestOrders"
import { CreateListingToast } from "../../ToastHandler/CreateListingToast/CreateListingToast"
import {
  CREATOR_FEE_ENFORCED_LOCAL_STORAGE,
  DEFAULT_EXPIRATION,
  LISTING_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE,
  LISTING_EXPIRATION_LOCAL_STORAGE,
} from "../constants"
import { ListingConfirmPriceModal } from "./components/ListingConfirmPriceModal"
import { ListingFlowDone } from "./components/ListingFlowDone"
import { ListingFlowDone_itemFragment } from "./components/ListingFlowDone/ListingFlowDone.fragment"
import {
  ListingFlowForm,
  ListingFlowFormSkeleton,
} from "./components/ListingFlowForm"
import { ListingFlowForm_itemFragment } from "./components/ListingFlowForm/ListingFlowForm.fragment"
import { readItemHighestTraitFloor } from "./components/ListingFlowItemsTable/readItemHighestTraitFloor"
import { readItemHighestTraitFloorFragment } from "./components/ListingFlowItemsTable/readItemHighestTraitFloor.fragment"
import { ListingFlowReview } from "./components/ListingFlowReview/ListingFlowReview"
import { ListingFlowReview_itemFragment } from "./components/ListingFlowReview/ListingFlowReview.fragment"
import { listingFlowFormSchema } from "./schema"
import type { ListingFlowFormValues } from "./types"

const ListingFlowBaseFragment = graphql(
  `
    fragment ListingFlowBase on Item {
      id
      imageUrl
      chain {
        identifier
      }
      collection {
        id
        fees {
          totalCreatorFee {
            feeBasisPoints
            isRequired
          }
          openseaFee {
            feeBasisPoints
          }
        }

        floorPrice {
          pricePerItem {
            token {
              unit
            }
          }
        }
      }
      chain {
        listingCurrency {
          symbol
        }
      }
      isFungible
      ownership(address: $address) {
        quantity
      }
      lowestListingForOwner(address: $address) {
        pricePerItem {
          token {
            unit
          }
        }
      }
      bestListing {
        pricePerItem {
          token {
            unit
          }
        }
      }
      ...ListingFlowForm_item
      ...ListingFlowDone_item
      ...ListingFlowReview_item
      ...useLiveUpdateItemsBestOrders
      ...readItemHighestTraitFloor
    }
  `,
  [
    ListingFlowForm_itemFragment,
    ListingFlowDone_itemFragment,
    ListingFlowReview_itemFragment,
    useLiveUpdateItemsBestOrdersFragment,
    readItemHighestTraitFloorFragment,
  ],
)

const ListingFlowQuery = graphql(
  `
    query ListingFlowQuery(
      $identifiers: [ItemIdentifierInput!]!
      $address: Address
    ) {
      itemsByIdentifiers(identifiers: $identifiers) {
        id
        tokenId
        contractAddress
        chain {
          identifier
        }
        ...ListingFlowBase
        ...isItemTradable
      }
    }
  `,
  [ListingFlowBaseFragment, isItemTradableFragment],
)

type Props = Pick<ListingFlowState, "items">
type WithQuantity<T> = T & { quantity: number }

export function ListingFlow({ items: itemsInput }: Props) {
  const address = useAddress()
  const [{ data, fetching }] = useQuery({
    query: ListingFlowQuery,
    variables: {
      identifiers: itemsInput.map(item => ({
        tokenId: item.tokenId,
        contractAddress: item.contractAddress,
        chain: item.chain.identifier,
      })),
      address,
    },
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })

  useEndFlowOnAccountChange()

  const anyItemDisabled = data?.itemsByIdentifiers.some(
    item => !isItemTradable(item),
  )
  if (anyItemDisabled) {
    throw new Error("One or more items disabled for trading.")
  }
  const items = data?.itemsByIdentifiers.flatMap(item => {
    const quantity = itemsInput.find(
      itemInput =>
        itemInput.tokenId === item?.tokenId &&
        itemInput.chain.identifier === item.chain.identifier &&
        itemInput.contractAddress === item.contractAddress,
    )?.quantity
    return item && quantity ? [{ ...item, quantity }] : []
  })

  if (fetching) {
    return <ListingFlowFormSkeleton count={itemsInput.length} />
  }

  if (!items) {
    throw new Error("There was a problem with listing.")
  }

  return <ListingFlowBase items={items} />
}

const ListingFlowBase = ({
  items: itemKeys,
}: {
  items: WithQuantity<FragmentOf<typeof ListingFlowBaseFragment>>[]
}) => {
  const t = useTranslations("wallet.ListingFlow")
  const { addCustomToast } = useToasts()
  const [view, setView] = useState<"create" | "review" | "success" | "error">(
    "create",
  )
  const { flow } = useSnapshot(FlowState)
  const mode = (flow as ListingFlowState)?.mode
  const [isPriceWarningModalOpen, setIsPriceWarningModalOpen] = useState(false)

  const items = useLiveUpdateItemsBestOrders(
    itemKeys.map(item => ({
      ...readFragment(ListingFlowBaseFragment, item),
      quantity: item.quantity,
    })),
  )

  const methods = useForm({
    defaultValues: useDefaultFormValues(items),
    mode: "onChange",
    resolver: zodResolver(listingFlowFormSchema),
  })
  const listings = useWatch({ control: methods.control, name: "listings" })
  const listingItems = useMemo(
    () =>
      items.filter(item => listings.find(listing => listing.id === item.id)),
    [items, listings],
  )

  const showToast = useCallback(
    (success: boolean) => {
      addCustomToast(
        () => (
          <CreateListingToast
            imageUrls={listingItems.map(item => item.imageUrl || "")}
            price={{
              unit: listings[0].price,
              symbol: listings[0].listingSymbol,
            }}
            success={success}
          />
        ),
        {
          id: "create-listing-toast",
          duration: 15_000,
        },
      )
    },
    [listingItems, listings, addCustomToast],
  )

  useSyncFormFields({
    fields: items.flatMap((item, index) => [
      {
        name: `listings.${index}.floorPrice` as const,
        value: item.isFungible
          ? item.bestListing?.pricePerItem.token.unit
          : item.collection?.floorPrice?.pricePerItem.token.unit,
      },
      {
        name: `listings.${index}.traitFloorPrice` as const,
        value: readItemHighestTraitFloor(item)?.token.unit,
      },
    ]),
    setValue: methods.setValue,
  })

  const handleFormSubmit = () => {
    const highSevPriceWarningsCount = listings.filter(
      listing => listing.shouldConfirmPrice,
    ).length

    if (highSevPriceWarningsCount > 0) {
      setIsPriceWarningModalOpen(true)
    } else {
      setView("review")
    }
  }

  return (
    <FormProvider {...methods}>
      {view === "create" && (
        <ListingFlowForm items={items} onSubmit={handleFormSubmit} />
      )}
      {view === "review" && (
        <ListingFlowReview
          items={listingItems}
          onBack={() => setView("create")}
          onError={() => {
            if (mode === "minimized") {
              showToast(false)
              FlowActions.endFlow()
            }

            setView("error")
          }}
          onSuccess={() => {
            FlowActions.onSuccess()

            if (mode === "minimized") {
              showToast(true)
              FlowActions.endFlow()
            }

            setView("success")
          }}
        />
      )}
      {view === "success" && (
        <ListingFlowDone
          items={listingItems}
          title={t("successTitle")}
          withLink
        />
      )}
      {view === "error" && (
        <ListingFlowDone
          body={t("errorBody")}
          items={listingItems}
          title={t("errorTitle")}
          withError
        />
      )}
      <ListingConfirmPriceModal
        onNext={() => setView("review")}
        open={isPriceWarningModalOpen}
        setIsOpen={setIsPriceWarningModalOpen}
      />
    </FormProvider>
  )
}

const useDefaultFormValues = (
  items: WithQuantity<ResultOf<typeof ListingFlowBaseFragment>>[],
): ListingFlowFormValues => {
  const [localStorageCreatorFeeEnforced] = useLocalStorage(
    CREATOR_FEE_ENFORCED_LOCAL_STORAGE,
    true,
  )
  const [localStorageListingExpiration] = useLocalStorage(
    LISTING_EXPIRATION_LOCAL_STORAGE,
    DEFAULT_EXPIRATION,
  )
  const [localStorageListingExpirationCustom] = useLocalStorage<
    string | undefined
  >(LISTING_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE, undefined)

  return {
    adjustForFees: false,
    applyCreatorFee:
      items.some(item => item.collection?.fees.totalCreatorFee.isRequired) ||
      localStorageCreatorFeeEnforced,
    expiration: {
      expirationType: localStorageListingExpiration,
      customExpiration: localStorageListingExpirationCustom
        ? new Date(localStorageListingExpirationCustom)
        : undefined,
    },
    reservedBuyer: {
      identifier: undefined,
      resolvedAddress: undefined,
      isResolving: false,
      currentAddress: undefined,
    },
    listings: items.map(item => {
      const ownedQuantity = item.ownership?.quantity ?? "0"
      return {
        id: item.id,
        quantity: item.quantity,
        creatorFeeBasisPoints:
          item.collection?.fees.totalCreatorFee.feeBasisPoints || 0,
        openSeaFeeBasisPoints:
          item.collection?.fees.openseaFee?.feeBasisPoints || 0,
        price:
          item.lowestListingForOwner?.pricePerItem.token.unit || Number.NaN,
        floorPrice: item.isFungible
          ? item.bestListing?.pricePerItem.token.unit
          : item.collection?.floorPrice?.pricePerItem.token.unit,
        isCreatorFeeRequired: Boolean(
          item.collection?.fees.totalCreatorFee.isRequired,
        ),
        applyCreatorFee:
          item.collection?.fees.totalCreatorFee.isRequired ||
          localStorageCreatorFeeEnforced,
        ownedQuantity: item.isFungible ? +ownedQuantity : 1,
        listingSymbol: item.chain.listingCurrency.symbol,
      }
    }),
  }
}
